import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService, ConfigurationService, User, UserService, UUID} from '@nh/angular-tses';
import {ROLE_METADATA} from 'app/permissions/role-metadata';
import {DepartureRole, DepartureRolesArray} from 'app/permissions/types';
import {AssignableRole} from 'app/transition/modals/user-assign-modal/user-assign-modal.component';
import {toArray} from 'app/utils/array-utils';
import {of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DeparturePermissions, getAllAssignedUserIds} from '../../permissions/departure-permissions';

export interface Reassignment {
  role: DepartureRole;
  user: (User | string)[] | User | string;
}

@Injectable({
  providedIn: 'root'
})
export class RoleService extends BaseService {
  constructor(
    _snackbar: MatSnackBar,
    _configurationService: ConfigurationService,
    private _http: HttpClient,
    private _userService: UserService
  ) {
    super(_snackbar, _configurationService);
  }

  /**
   * Gets all the users which have the permissions to be assigned to a given role.
   * This will include users already assigned to that role.
   *
   * @param roles {DepartureRole[]}             The roles you are interested in
   * @returns     {Promise<AssignableRole[]>}   An array of role-users pairs
   */
  public getPotentialAssignees(roles: DepartureRole[]): Promise<AssignableRole[]> {
    if (!roles || !roles.length) {
      return Promise.resolve([]);
    }

    const params: {[param: string]: string[]} = {roles};

    return this._http
      .get(`${this.config.apiHost}/role`, {params})
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this._handleError(err, `Failed to fetch users for roles: ${roles.join(', ')}`, 'role-fetch-error');
          return of([]);
        }),
        map((json: any) => {
          return roles
            .sort((role1: DepartureRole, role2: DepartureRole) => {
              return ROLE_METADATA[role1].order - ROLE_METADATA[role2].order;
            })
            .map((role: DepartureRole) => {
              const users: User[] = toArray(json[role]).map((userJson: any) => User.deserialise(userJson));
              return {role, users};
            });
        })
      )
      .toPromise();
  }

  /**
   * Get the user objects of the current assignees of a permissions object.
   * This can optionally be filtered to a subset of the existing roles.
   *
   * @param permissions {DeparturePermissions}        The DeparturePermissions to filter
   * @param rolesToGet  {string[]}                    The roles you are interested in.
   * @returns           {Promise<AssignableRole[]>}   Array of role-users pairs
   */
  public getCurrentAssignees(
    permissions: DeparturePermissions,
    rolesToGet: string[] = DepartureRolesArray
  ): Promise<AssignableRole[]> {
    if (!permissions || !rolesToGet || !rolesToGet.length) {
      return Promise.resolve([]);
    }

    const ids: string[] = getAllAssignedUserIds(permissions, rolesToGet);
    const promises: Promise<User>[] = ids.map((id: string) =>
      this._userService.getUserFromId(UUID.orNull(id)).toPromise()
    );

    return Promise.all(promises).then((users: User[]) => {
      const idToUser: Record<string, User> = {};
      users.forEach(u => {
        if (u && u.id) {
          idToUser[u.id.value] = u;
        }
      });

      return rolesToGet
        .map((role: DepartureRole) => {
          if (permissions[role]) {
            const usersInRole: User[] = toArray(permissions[role])
              .map((id: string) => idToUser[id])
              .filter((u: User) => !!u);
            return {role: role, users: usersInRole};
          } else {
            return null;
          }
        })
        .filter((assignee: AssignableRole) => assignee && assignee.users.length);
    });
  }

  public getCurrentAssigneesArray(
    permissions: DeparturePermissions,
    rolesToGet: string[] = DepartureRolesArray
  ): Promise<User[]> {
    return this.getCurrentAssignees(permissions, rolesToGet).then((currentRoleAssignments: AssignableRole[]) => {
      const allCurrentAssignees: Set<User> = new Set<User>();
      currentRoleAssignments.forEach((currentRoleAssignees: AssignableRole) => {
        currentRoleAssignees.users.forEach((user: User) => allCurrentAssignees.add(user));
      });
      return Array.from(allCurrentAssignees.values());
    });
  }
}
